import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ProductCard } from '../components/ProductCard';
import { StoreCard } from '../components/StoreCard';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { useRecentlyViewed } from '../hooks/useRecentlyViewed';
import { ArrowRight, Heart, Package, Users, Clock } from 'lucide-react';
import { StarDoodle, SparkleDoodle, SquiggleDoodle, LeafDoodle, BlobDoodle } from '../components/Doodles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categoryData = {
  crochet: {
    url: 'https://images.unsplash.com/photo-1615733449290-6d88928ddee7?auto=format&fit=crop&q=80&w=400',
    label: 'Crochet',
    color: 'from-pink-200 to-pink-100',
    textColor: 'text-pink-700'
  },
  stickers: {
    url: 'https://images.unsplash.com/photo-1609560553235-12eb528be6c3?auto=format&fit=crop&q=80&w=400',
    label: 'Stickers',
    color: 'from-yellow-200 to-yellow-100',
    textColor: 'text-yellow-700'
  },
  art: {
    url: 'https://images.unsplash.com/photo-1581664369289-b78660d34746?auto=format&fit=crop&q=80&w=400',
    label: 'Art',
    color: 'from-purple-200 to-purple-100',
    textColor: 'text-purple-700'
  },
  keyrings: {
    url: 'https://images.unsplash.com/photo-1588987617819-c04a0d4b0233?auto=format&fit=crop&q=80&w=400',
    label: 'Keyrings',
    color: 'from-orange-200 to-orange-100',
    textColor: 'text-orange-700'
  }
};

export default function HomePage() {
  const [products, setProducts] = useState([]);
  const [stores, setStores] = useState([]);
  const [stats, setStats] = useState({ products: 0, stores: 0, creators: 0 });
  const [loading, setLoading] = useState(true);
  const { recentlyViewed } = useRecentlyViewed();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productsRes, storesRes, statsRes] = await Promise.all([
        fetch(`${API}/products/featured`),
        fetch(`${API}/stores?limit=4`),
        fetch(`${API}/stats`)
      ]);

      if (productsRes.ok) setProducts(await productsRes.json());
      if (storesRes.ok) setStores(await storesRes.json());
      if (statsRes.ok) setStats(await statsRes.json());
    } catch (e) {
      console.error('Error loading home data:', e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero - Cream brand surface */}
      <section className="relative overflow-hidden py-14 md:py-20 bg-background text-foreground">
        {/* Hand-drawn doodle accents */}
        <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
          <StarDoodle className="absolute top-14 left-[9%] w-11 text-periwinkle -rotate-12 animate-fade-in" />
          <SparkleDoodle className="absolute top-24 right-[13%] w-9 text-mustard animate-fade-in" />
          <LeafDoodle className="absolute top-1/2 left-[5%] w-9 text-primary/50 -rotate-12" />
          <SquiggleDoodle className="absolute bottom-28 left-[15%] w-20 text-secondary/70" />
          <BlobDoodle className="absolute bottom-14 right-[8%] w-16 text-peach/60 rotate-6" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="block uppercase tracking-[0.22em] text-xs md:text-sm font-bold text-text-tertiary mb-4" data-testid="hero-eyebrow">
              a marketplace for neurodivergent creatives
            </span>
            <h1 className="font-display font-black tracking-tight leading-[1.02] mb-5 animate-fade-in text-foreground text-5xl sm:text-6xl lg:text-7xl">
              your mind works differently.{' '}
              <span className="text-primary">good.</span>
            </h1>
            <p className="text-lg text-text-secondary max-w-xl mx-auto mb-7 leading-relaxed">
              Discover and commission unique crochet, stickers, art, and keyrings from makers whose brains don't do straight lines.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <Link to="/browse">
                <Button className="lowercase font-display font-bold bg-primary text-primary-foreground hover:bg-chilli-deep active:scale-95 rounded-full px-8 py-6 text-lg transition-all" data-testid="hero-browse-btn">
                  explore work
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <Link to="/register">
                <Button variant="outline" className="lowercase font-display font-bold border-2 border-foreground/25 text-foreground bg-transparent hover:bg-foreground/5 hover:text-foreground active:scale-95 rounded-full px-8 py-6 text-lg transition-all" data-testid="hero-sell-btn">
                  sell your work
                </Button>
              </Link>
            </div>
          </div>

          {/* Category Cards - Colourful and inviting */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {Object.entries(categoryData).map(([cat, data], i) => (
              <Link 
                key={cat}
                to={`/browse?category=${cat}`}
                className="group relative rounded-2xl overflow-hidden h-40 md:h-48 shadow-sm hover:shadow-md transition-all animate-fade-in"
                style={{ animationDelay: `${i * 0.1}s` }}
                data-testid={`category-${cat}`}
              >
                <img 
                  src={data.url} 
                  alt={cat}
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-ink/25 to-transparent" />
                <div className="absolute inset-0 flex items-end p-4">
                  <span className="font-heading font-bold text-lg text-warm-white">
                    {data.label}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats - Fun colours */}
      <section className="py-10 border-y border-border">
        <div className="max-w-5xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-3 gap-6 text-center">
            <div className="animate-fade-in">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-chilli/10 mb-2">
                <Package className="w-5 h-5 text-chilli" />
              </div>
              <p className="font-heading text-2xl font-bold">{stats.products}</p>
              <p className="text-sm text-muted-foreground">Products</p>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-bluebell/10 mb-2">
                <Users className="w-5 h-5 text-bluebell" />
              </div>
              <p className="font-heading text-2xl font-bold">{stats.creators}</p>
              <p className="text-sm text-muted-foreground">Creators</p>
            </div>
            <div className="animate-fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-fern/10 mb-2">
                <Heart className="w-5 h-5 text-fern" />
              </div>
              <p className="font-heading text-2xl font-bold">{stats.stores}</p>
              <p className="text-sm text-muted-foreground">Stores</p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Products - The main event */}
      <section className="py-14">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between mb-8">
            <h2 className="relative inline-flex items-center gap-2 text-3xl md:text-4xl font-display font-extrabold tracking-tight lowercase">
              fresh finds
              <StarDoodle className="w-6 h-6 text-mustard shrink-0" />
            </h2>
            <Link to="/browse">
              <Button variant="ghost" className="group" data-testid="view-all-products">
                View all
                <ArrowRight className="w-4 h-4 ml-1 transition-transform group-hover:translate-x-1" />
              </Button>
            </Link>
          </div>
          
          {loading ? (
            <LoadingSpinner />
          ) : products.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {products.slice(0, 8).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="text-center py-12 rounded-2xl bg-gradient-to-br from-mustard-tint to-fern-tint">
              <p className="text-muted-foreground mb-4">No products yet. Be the first to add one!</p>
              <Link to="/register">
                <Button>Become a creator</Button>
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Recently Viewed - Personal touch */}
      {recentlyViewed.length > 0 && (
        <section className="py-14 bg-bluebell-tint">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex items-center gap-3 mb-8">
              <div className="p-2 rounded-full bg-primary/10">
                <Clock className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-3xl md:text-4xl font-display font-extrabold tracking-tight lowercase">recently viewed</h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {recentlyViewed.slice(0, 4).map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Creators */}
      {stores.length > 0 && (
        <section className="py-14 bg-lavender">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="relative inline-flex items-center gap-2 text-3xl md:text-4xl font-display font-extrabold tracking-tight lowercase text-text-primary">
                meet the creators
                <SparkleDoodle className="w-6 h-6 text-text-primary/70 shrink-0" />
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
              {stores.map((store) => (
                <StoreCard key={store.id} store={store} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Simple CTA */}
      <section className="relative overflow-hidden py-16 md:py-20 bg-peach">
        <div className="pointer-events-none absolute inset-0 hidden md:block" aria-hidden="true">
          <BlobDoodle className="absolute top-10 left-[12%] w-14 text-primary/25 -rotate-12" />
          <StarDoodle className="absolute bottom-12 right-[14%] w-10 text-text-primary/40" />
        </div>
        <div className="relative max-w-2xl mx-auto px-4 md:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-display font-extrabold tracking-tight lowercase mb-3 text-text-primary">
            got something to share?
          </h2>
          <p className="text-text-primary/75 mb-6 leading-relaxed">
            Join our community of neurodivergent creators. No pressure, no algorithms, just a friendly place to sell your work.
          </p>
          <Link to="/register">
            <Button className="lowercase font-display font-bold bg-chilli text-butter hover:bg-chilli-deep active:scale-95 rounded-full px-8 py-6 text-lg transition-all" data-testid="cta-start-selling">
              start selling
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
